/**
 * useTrackingStore - Zustand store for live recording state.
 *
 * Bridges the TrackingService singleton with React components.
 * Does NOT use persist middleware since recording state is ephemeral.
 *
 * @typedef {import('../types/index.js').GpsPoint} GpsPoint
 * @typedef {import('../types/index.js').Session} Session
 */

import { create } from 'zustand';
import {
  getTrackingService,
  RecordingState,
  TrackingEvents,
} from '../tracking/TrackingService.js';
import { calculateLiveStats } from '../tracking/StatsCalculator.js';
import { classifyCurrentActivity } from '../tracking/SegmentClassifier.js';
import { loadActiveSession } from '../services/db/meta.js';

export const useTrackingStore = create((set, get) => {
  // ─── Internal: wire up TrackingService events ─────────────────────────
  let _unsubscribers = [];

  function _subscribeToService() {
    const service = getTrackingService();

    _unsubscribers.push(
      service.on(TrackingEvents.STATE_CHANGE, ({ to }) => {
        set({ recordingState: to });
      })
    );

    _unsubscribers.push(
      service.on(TrackingEvents.GPS_POINT, (point) => {
        const state = get();
        const updatedPoints = [...state.currentPoints, point];
        set({
          currentPoints: updatedPoints,
          currentSpeed: point.speed || 0,
          currentAltitude: point.altitude || 0,
        });
      })
    );

    _unsubscribers.push(
      service.on(TrackingEvents.LIVE_STATS, (liveStats) => {
        // Map live stats to both the new liveStats shape and the legacy stats shape
        set({
          liveStats,
          stats: {
            totalRuns: liveStats.runs || 0,
            totalDistance: liveStats.distance || 0,
            totalVertical: liveStats.vertical || 0,
            maxSpeed: liveStats.topSpeed || 0,
            avgSpeed: 0,
            totalDuration: 0,
            activeTime: liveStats.timeSkiing || 0,
            liftTime: liveStats.timeOnLifts || 0,
            pauseTime: 0,
            longestRun: 0,
            biggestVertical: 0,
          },
        });
      })
    );

    _unsubscribers.push(
      service.on(TrackingEvents.ACTIVITY_CHANGE, (activity) => {
        set({ currentActivity: activity });
      })
    );

    _unsubscribers.push(
      service.on(TrackingEvents.SESSION_COMPLETE, (session) => {
        set({
          currentSession: session,
          segments: session?.segments || [],
          stats: session?.stats || get().stats,
        });
      })
    );

    _unsubscribers.push(
      service.on(TrackingEvents.ERROR, (error) => {
        console.error('[TrackingStore] Error:', error);
        set({ lastError: error });
      })
    );
  }

  function _unsubscribeFromService() {
    for (const unsub of _unsubscribers) {
      unsub();
    }
    _unsubscribers = [];
  }

  // ─── Timer for elapsed time updates ───────────────────────────────────
  let _elapsedTimerId = null;

  function _startElapsedTimer() {
    _stopElapsedTimer();
    _elapsedTimerId = setInterval(() => {
      const service = getTrackingService();
      const t = service.elapsedTime;
      set({ elapsedTime: t, elapsed: t });
    }, 1000);
  }

  function _stopElapsedTimer() {
    if (_elapsedTimerId != null) {
      clearInterval(_elapsedTimerId);
      _elapsedTimerId = null;
    }
  }

  // Default empty stats shape
  const EMPTY_LIVE_STATS = {
    runs: 0,
    vertical: 0,
    topSpeed: 0,
    distance: 0,
    timeSkiing: 0,
    timeOnLifts: 0,
  };

  const EMPTY_ACTIVITY = {
    type: 'stopped',
    speed: 0,
    altitude: 0,
  };

  return {
    // ─── State ─────────────────────────────────────────────────────────
    recordingState: RecordingState.IDLE,

    // Backward-compat flags (derived from recordingState for existing pages)
    isRecording: false,
    isPaused: false,

    currentSession: null,
    currentPoints: [],

    // Live position
    currentSpeed: 0,
    currentAltitude: 0,

    liveStats: { ...EMPTY_LIVE_STATS },
    currentActivity: { ...EMPTY_ACTIVITY },
    elapsedTime: 0,
    lastError: null,

    // Resort context
    resortId: null,
    resortName: null,

    // ─── Actions ───────────────────────────────────────────────────────

    /**
     * Start a new recording session.
     * @param {string} [resortId]
     * @param {string} [resortName]
     */
    startRecording: (resortId, resortName) => {
      const now = Date.now();
      // Reset state
      set({
        currentSession: null,
        currentPoints: [],
        liveStats: { ...EMPTY_LIVE_STATS },
        currentActivity: { ...EMPTY_ACTIVITY },
        elapsedTime: 0,
        elapsed: 0,
        startTime: now,
        lastError: null,
        currentSpeed: 0,
        currentAltitude: 0,
        isRecording: true,
        isPaused: false,
        resortId: resortId || null,
        resortName: resortName || null,
        segments: [],
        stats: {
          totalRuns: 0, totalDistance: 0, totalVertical: 0, maxSpeed: 0,
          avgSpeed: 0, totalDuration: 0, activeTime: 0, liftTime: 0,
          pauseTime: 0, longestRun: 0, biggestVertical: 0,
        },
      });

      // Wire up event listeners
      _subscribeToService();

      const service = getTrackingService();
      service.startRecording();
      _startElapsedTimer();
    },

    /**
     * Stop the current recording and process it.
     * @returns {Promise<Session | null>}
     */
    stopRecording: async () => {
      _stopElapsedTimer();
      const service = getTrackingService();
      const session = await service.stopRecording();
      _unsubscribeFromService();
      set({ isRecording: false, isPaused: false, startTime: null });
      return session;
    },

    /**
     * Pause the recording.
     */
    pauseRecording: () => {
      _stopElapsedTimer();
      const service = getTrackingService();
      service.pauseRecording();
      set({ isPaused: true });
    },

    /**
     * Resume a paused recording.
     */
    resumeRecording: () => {
      const service = getTrackingService();
      service.resumeRecording();
      _startElapsedTimer();
      set({ isPaused: false });
    },

    /**
     * Discard the current session.
     */
    discardRecording: () => {
      _stopElapsedTimer();
      _unsubscribeFromService();
      const service = getTrackingService();
      service.discardRecording();
      set({
        recordingState: RecordingState.IDLE,
        isRecording: false,
        isPaused: false,
        currentSession: null,
        currentPoints: [],
        liveStats: { ...EMPTY_LIVE_STATS },
        currentActivity: { ...EMPTY_ACTIVITY },
        elapsedTime: 0,
        lastError: null,
        currentSpeed: 0,
        currentAltitude: 0,
        resortId: null,
        resortName: null,
      });
    },

    /**
     * Attempt to restore an in-progress session from Dexie meta. Called once
     * at app boot. If a snapshot is found, rebuilds TrackingService state and
     * resumes the elapsed-time timer / GPS tracking.
     * @returns {Promise<boolean>} true if a session was restored
     */
    restoreActiveSession: async () => {
      try {
        const snap = await loadActiveSession();
        if (!snap || !snap.sessionId) return false;
        const service = getTrackingService();
        const ok = service.restoreFromSnapshot(snap);
        if (!ok) return false;
        _subscribeToService();
        const isRecording = snap.state === RecordingState.RECORDING;
        const isPaused = snap.state === RecordingState.PAUSED;
        set({
          recordingState: snap.state,
          isRecording: isRecording || isPaused,
          isPaused,
          startTime: snap.startTime || null,
          elapsedTime: service.elapsedTime,
          elapsed: service.elapsedTime,
          currentPoints: Array.isArray(snap.rawPoints) ? snap.rawPoints : [],
          resortId: snap.resortId || null,
          resortName: snap.resortName || null,
        });
        if (isRecording) _startElapsedTimer();
        return true;
      } catch (err) {
        console.warn('[TrackingStore] restoreActiveSession failed:', err?.message || err);
        return false;
      }
    },

    /**
     * Manually add a GPS point (useful for testing / simulation).
     * @param {GpsPoint} point
     */
    addGpsPoint: (point) => {
      const { currentPoints } = get();
      set({
        currentPoints: [...currentPoints, point],
        currentSpeed: point.speed || 0,
        currentAltitude: point.altitude || 0,
      });
    },

    /**
     * Force a live stats recalculation from the current point buffer.
     */
    updateLiveStats: () => {
      const { currentPoints } = get();
      const stats = calculateLiveStats(currentPoints);
      set({ liveStats: stats });

      // Also update current activity
      const recent = currentPoints.slice(-10);
      const activity = classifyCurrentActivity(recent);
      set({ currentActivity: activity });
    },

    // ─── Backward-compat fields and aliases for useTracking hook ──────

    /** Alias: elapsed in ms (same as elapsedTime, referenced by useTracking) */
    elapsed: 0,
    /** Alias: startTime timestamp (referenced by useTracking) */
    startTime: null,
    /** Completed segments so far (populated after stop/processing) */
    segments: [],
    /** Stats object in the shape the Record page expects */
    stats: {
      totalRuns: 0,
      totalDistance: 0,
      totalVertical: 0,
      maxSpeed: 0,
      avgSpeed: 0,
      totalDuration: 0,
      activeTime: 0,
      liftTime: 0,
      pauseTime: 0,
      longestRun: 0,
      biggestVertical: 0,
    },

    // Backward-compat action aliases
    updatePosition: (point) => {
      get().addGpsPoint(point);
    },
    updateActivity: (activity) => set({ currentActivity: activity }),
    updateStats: (stats) => set({ stats: { ...stats } }),
    updateElapsed: (elapsed) => set({ elapsedTime: elapsed, elapsed }),
  };
});

export default useTrackingStore;
